import { FC, ReactNode } from "react";

import {
  Box,
  ChevronRightIcon,
  ChakraAccordion,
  ChakraAccordionButton,
  ChakraAccordionItem,
  ChakraAccordionPanel,
  Badge,
  Text,
  Column,
  Row,
  SystemStyleObject,
} from "@hightouch/htui";
import noop from "lodash/noop";
import { Markdown } from "src/ui/markdown";

interface Props {
  children: ReactNode;
  label: string | ReactNode;
  labelFontSize?: "sm" | "md" | "lg";
  description?: string;
  count?: number;
  onChange?: (...args: any[]) => void;
  buttonSx?: SystemStyleObject;
  expandedButtonSx?: SystemStyleObject;
  buttonPosition?: "left" | "right";
  noPadding?: boolean;
  index?: number;
  /** Keep collapsed content mounted (hidden) so PDF/print export can capture it. */
  keepContentMounted?: boolean;
}

export const AccordionSection: FC<Readonly<Props>> = ({
  children,
  label,
  labelFontSize = "md",
  count,
  description,
  onChange = noop,
  buttonSx,
  expandedButtonSx,
  buttonPosition = "left",
  noPadding = false,
  index,
  keepContentMounted = false,
}) => (
  <ChakraAccordion
    allowToggle
    onChange={onChange}
    index={index}
    // reduceMotion makes the collapsed panel a plain hidden element (no
    // framer-motion Collapse), which the print stylesheet can reveal cleanly.
    reduceMotion={keepContentMounted}
  >
    <ChakraAccordionItem border="none">
      {({ isExpanded }) => {
        return (
          <>
            <ChakraAccordionButton
              aria-label={isExpanded ? "Hide" : "View"}
              cursor="pointer"
              outline="none !important"
              pl={noPadding ? 0 : 2}
              borderRadius="md"
              sx={isExpanded ? expandedButtonSx : buttonSx}
            >
              <Column flex={1}>
                <Row align="center" gap={2} textAlign="left">
                  <Box
                    as={ChevronRightIcon}
                    fontSize="16px"
                    transform={isExpanded ? "rotate(90deg)" : ""}
                    transitionProperty="transform"
                    transitionDuration="fast"
                    transitionTimingFunction="easeOutQuad"
                    flexShrink={0}
                    order={buttonPosition === "right" ? 1 : 0}
                  />
                  <Row
                    align="center"
                    gap={2}
                    flex={buttonPosition === "left" ? 1 : undefined}
                  >
                    {typeof label === "string" ? (
                      <Text size={labelFontSize} fontWeight="medium">
                        <Markdown disableParagraphs>{label}</Markdown>
                      </Text>
                    ) : (
                      label
                    )}
                    {count && <Badge>{count}</Badge>}
                  </Row>
                </Row>
              </Column>
              {description && (
                <Text size="sm" color="text.secondary">
                  <Markdown disableParagraphs>{description}</Markdown>
                </Text>
              )}
            </ChakraAccordionButton>
            {(isExpanded || keepContentMounted) && (
              <ChakraAccordionPanel sx={{ px: 0, py: noPadding ? 0 : 2 }}>
                {children}
              </ChakraAccordionPanel>
            )}
          </>
        );
      }}
    </ChakraAccordionItem>
  </ChakraAccordion>
);
